import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { NextActionPopup } from "@/components/NextActions";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Brand } from "@/components/Brand";
import {
  LayoutDashboard, Users, UserCog, UserCircle, LogOut, Menu, X, FileText, Search, ChevronUp,
  UserPlus, FolderKanban, CheckSquare, Calendar, CalendarClock, Receipt, MessageSquare, BarChart3, Settings, MessagesSquare, Wallet, FileSignature, GraduationCap, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { NotificationBell } from "@/components/NotificationBell";
import { FEATURES } from "@/lib/features";

const CLIENT_NAV = [
  { to: "/portal", icon: LayoutDashboard, label: "Dashboard", end: true, mobile: true },
  { to: "/portal/applications", icon: FileText, label: "Applications", mobile: true },
  { to: "/portal/profile", icon: UserCircle, label: "Profile", mobile: true },
];

const ADMIN_NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, mobile: true },
  { to: "/admin/leads", icon: UserPlus, label: "Leads", perm: "view_leads", mobile: true },
  { to: "/admin/clients", icon: Users, label: "Clients", perm: "view_clients", mobile: true },
  { to: "/admin/applications", icon: FolderKanban, label: "Cases", perm: "view_cases", mobile: true },
  { to: "/admin/documents", icon: FileText, label: "Documents", perm: "view_documents", feature: "documents" },
  { to: "/admin/tasks", icon: CheckSquare, label: "Tasks", perm: "manage_tasks" },
  { to: "/admin/appointments", icon: CalendarClock, label: "Appointments", perm: "manage_appointments", mobile: true },
  { to: "/admin/billing", icon: Receipt, label: "Billing", perm: "view_billing" },
  { to: "/admin/short-term", icon: GraduationCap, label: "Courses" },
  { to: "/admin/esign", icon: FileSignature, label: "E-Sign" },
  { to: "/admin/expenses", icon: Wallet, label: "Expenses", adminOnly: true },
  { to: "/admin/messages", icon: MessagesSquare, label: "Team Chat", badge: "team" },
  { to: "/admin/communications", icon: MessageSquare, label: "Client Messages", perm: "view_communications" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports", perm: "view_reports" },
  { to: "/admin/staff", icon: UserCog, label: "Staff", adminOnly: true },
  { to: "/admin/settings", icon: Settings, label: "Settings", perm: "manage_settings" },
];

export function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const collapseKey = `sidebarCollapsed:${user?.id || "anon"}`;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(collapseKey) === "1");
  useEffect(() => { setCollapsed(localStorage.getItem(collapseKey) === "1"); }, [collapseKey]);
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem(collapseKey, c ? "0" : "1"); return !c; });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "staff";
  const perms = user?.permissions || [];

  let items = (isAdmin ? ADMIN_NAV : CLIENT_NAV).filter((i) => !i.feature || FEATURES[i.feature]);
  if (user?.role === "staff") {
    items = items.filter((i) => !i.adminOnly && (!i.perm || perms.includes(i.perm)));
  }

  const [teamUnread, setTeamUnread] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    const load = () => api.get("/team/unread").then((r) => setTeamUnread(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    window.addEventListener("team-read", load);
    return () => { clearInterval(t); window.removeEventListener("team-read", load); };
  }, [isAdmin]);
  const doLogout = async () => { await logout(); nav("/login"); };
  const mobileItems = items.filter((i) => i.mobile).slice(0, 5);

  const NavItems = ({ onClick, mini = false }) => (
    <nav className="space-y-1">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onClick} title={mini ? it.label : undefined}
          data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `relative flex items-center gap-3 ${mini ? "justify-center px-0" : "px-3.5"} py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-white shadow-md shadow-primary/20" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            }`}
        >
          <it.icon className="h-[18px] w-[18px] shrink-0" />
          {!mini && <span>{it.label}</span>}
          {it.badge === "team" && teamUnread > 0 && (mini
            ? <span data-testid="nav-messages-unread" className="absolute top-1.5 right-2 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" />
            : <span data-testid="nav-messages-unread" className="ml-auto bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{teamUnread > 99 ? "99+" : teamUnread}</span>)}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside data-testid="sidebar" className={`hidden md:flex ${collapsed ? "w-[76px] p-3" : "w-64 p-4"} shrink-0 flex-col border-r border-slate-200 bg-white fixed h-screen transition-[width] duration-200`}>
        <div className={`py-2 mb-4 shrink-0 ${collapsed ? "flex justify-center" : "px-2"}`}><Brand showText={!collapsed} size={collapsed ? "sm" : undefined} /></div>
        <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar -mr-1 pr-1">
          <NavItems mini={collapsed} />
        </div>
        <div className="shrink-0 mt-2 pt-4 border-t border-slate-200">
          <div className={`flex items-center gap-2.5 py-2 ${collapsed ? "justify-center" : "px-2"}`} title={collapsed ? user?.name : undefined}>
            <div className="h-9 w-9 shrink-0 rounded-full bg-secondary text-white flex items-center justify-center font-head font-bold text-sm">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            {!collapsed && <div className="min-w-0 text-left flex-1">
              <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
            </div>}
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white p-4 flex flex-col animate-fadeup">
            <div className="flex items-center justify-between mb-4 shrink-0"><Brand /><button onClick={() => setSidebarOpen(false)}><X className="h-5 w-5 text-slate-500" /></button></div>
            <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar -mr-1 pr-1">
              <NavItems onClick={() => setSidebarOpen(false)} />
            </div>
            <button onClick={doLogout} className="shrink-0 mt-2 flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-rose-600"><LogOut className="h-4 w-4" /> Sign out</button>
          </aside>
        </div>
      )}

      <div className={`flex-1 ${collapsed ? "md:ml-[76px]" : "md:ml-64"} min-w-0 flex flex-col transition-[margin] duration-200`}>
        <header className="sticky top-0 z-30 glass border-b border-slate-200 px-4 md:px-8 py-3 flex items-center gap-3">
          <button className="md:hidden" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6 text-slate-700" /></button>
          <button className="hidden md:inline-flex p-1.5 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900" onClick={toggleCollapsed}
            data-testid="sidebar-collapse-btn" title={collapsed ? "Expand side panel" : "Collapse side panel"}>
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          <div className="md:hidden"><Brand showText={false} size="sm" /></div>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && <NotificationBell />}
            <div className="relative">
              <button data-testid="account-menu-btn" onClick={() => setUserMenuOpen((o) => !o)} className="hidden sm:flex items-center gap-2.5 pl-3 pr-2 py-1 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
                <span className="text-sm font-medium text-slate-600">{user?.name}</span>
                <div className="h-7 w-7 rounded-full bg-secondary text-white flex items-center justify-center text-xs font-bold">{user?.name?.[0]?.toUpperCase()}</div>
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl p-1.5 z-50 animate-fadeup" data-testid="account-menu">
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
                      <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
                    </div>
                    <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>
        <NextActionPopup />

        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex justify-around py-1.5 px-1">
          {mobileItems.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={`bottomnav-${it.label.toLowerCase()}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg min-w-[3.5rem] ${isActive ? "text-primary" : "text-slate-400"}`}>
              <it.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{it.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); setOpen(false); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setRes(r.data); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { setOpen(false); setQ(""); setRes(null); nav(path); };
  const total = res ? res.clients.length + res.applications.length + res.invoices.length : 0;
  const Group = ({ title, children }) => (
    <div><div className="px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</div>{children}</div>
  );
  return (
    <div className="relative flex-1 max-w-md hidden sm:block" onBlur={() => setTimeout(() => setOpen(false), 150)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input data-testid="global-search" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
        placeholder="Search clients, cases, UCI, passport, invoice #..."
        className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-white" />
      {open && res && (
        <div className="absolute mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-96 overflow-auto p-1.5" data-testid="search-results">
          {total === 0 && <div className="p-3 text-sm text-slate-400">No matches for "{q}"</div>}
          {res.clients.length > 0 && <Group title="Clients">{res.clients.map((c) => (
            <button key={c.id} onMouseDown={() => go(`/admin/clients/${c.id}`)} className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400 shrink-0" /><span className="font-medium text-slate-800 truncate">{c.full_name}</span>{c.inactive && <span data-testid={`search-inactive-${c.id}`} className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0">Inactive</span>}<span className="text-xs text-slate-400 truncate">{c.email}{c.uci ? ` · UCI ${c.uci}` : ""}</span>
            </button>))}</Group>}
          {res.applications.length > 0 && <Group title="Cases">{res.applications.map((a) => (
            <button key={a.id} onMouseDown={() => go(`/admin/applications/${a.id}`)} className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-2">
              <FolderKanban className="h-4 w-4 text-slate-400 shrink-0" /><span className="font-medium text-slate-800 truncate">{a.service_type}</span><span className="text-xs font-mono text-slate-400">{a.app_number}</span><span className="text-xs text-slate-400 truncate">· {a.client_name}</span>
            </button>))}</Group>}
          {res.invoices.length > 0 && <Group title="Invoices">{res.invoices.map((i) => (
            <button key={i.id} onMouseDown={() => go(`/admin/billing`)} className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-slate-50 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-slate-400 shrink-0" /><span className="font-mono text-sm text-slate-800">{i.number}</span><span className="text-xs text-slate-400 truncate">· {i.client_name} · ${i.total?.toFixed?.(2)}</span>
            </button>))}</Group>}
        </div>
      )}
    </div>
  );
}
